/**
 * Version History Panel Component
 *
 * Displays a timeline view of slide versions with ability to:
 * - View version details (timestamp, message, changes)
 * - Restore to any previous version
 * - Compare versions
 * - Delete old versions
 */

import React, { useState } from 'react';
import { Clock, RotateCcw, Trash2, GitCompare, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { cn } from '../../lib/utils';

export interface VersionInfo {
  version: number;
  versionId?: string; // UUIDv7 - globally unique identifier (optional for backwards compatibility)
  timestamp: string;
  message: string;
  author: string;
  changes: {
    type: 'create' | 'edit' | 'restore';
    summary: string;
    elements?: string[];
  };
}

interface VersionHistoryPanelProps {
  conversationId: string;
  slideId: string;
  versions: VersionInfo[];
  currentVersion: number | null;
  onRestore: (version: number) => void;
  onDelete: (version: number) => void;
  onCompare: (v1: number, v2: number) => void;
  isRestoring: boolean;
  className?: string;
}

export const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
  versions,
  currentVersion,
  onRestore,
  onDelete,
  onCompare,
  isRestoring,
  className = '',
}) => {
  const [selectedVersions, setSelectedVersions] = useState<number[]>([]);
  const [deletingVersion, setDeletingVersion] = useState<number | null>(null);

  // Format timestamp for display
  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  // Format full timestamp for tooltip
  const formatFullTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  // Handle version selection for comparison
  const handleVersionClick = (version: number) => {
    if (selectedVersions.includes(version)) {
      setSelectedVersions(selectedVersions.filter(v => v !== version));
    } else if (selectedVersions.length < 2) {
      setSelectedVersions([...selectedVersions, version]);
    } else {
      // Replace oldest selection
      setSelectedVersions([selectedVersions[1], version]);
    }
  };

  // Handle compare action
  const handleCompare = () => {
    if (selectedVersions.length === 2) {
      onCompare(selectedVersions[0], selectedVersions[1]);
      setSelectedVersions([]);
    }
  };

  // Handle delete action
  const handleDelete = async (version: number) => {
    setDeletingVersion(version);
    try {
      await onDelete(version);
    } finally {
      setDeletingVersion(null);
    }
  };

  // Sort versions in reverse order (newest first)
  const sortedVersions = [...versions].sort((a, b) => b.version - a.version);

  if (versions.length === 0) {
    return (
      <div className={cn('flex flex-col items-center justify-center py-12 text-center', className)}>
        <Clock className="h-12 w-12 text-muted-foreground/30 mb-3" />
        <p className="text-sm text-muted-foreground">No version history available</p>
        <p className="text-xs text-muted-foreground/70 mt-1">
          Versions will appear here after you make edits
        </p>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground mb-1">Version History</h3>
        <p className="text-xs text-muted-foreground">
          {versions.length} version{versions.length !== 1 ? 's' : ''} •
          Current: v{currentVersion}
        </p>

        {/* Compare button */}
        {selectedVersions.length === 2 && (
          <Button
            onClick={handleCompare}
            variant="outline"
            size="sm"
            className="w-full mt-3"
          >
            <GitCompare className="h-3 w-3 mr-2" />
            Compare v{selectedVersions[0]} and v{selectedVersions[1]}
          </Button>
        )}
      </div>

      {/* Version Timeline - Compact with ScrollArea */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-2">
          {sortedVersions.map((versionInfo, index) => {
            const isCurrent = versionInfo.version === currentVersion;
            const isSelected = selectedVersions.includes(versionInfo.version);
            const isDeleting = deletingVersion === versionInfo.version;

            return (
              <div
                key={versionInfo.versionId || `v${versionInfo.version}-${versionInfo.timestamp}`}
                className={cn(
                  'relative border rounded-lg p-2 transition-all',
                  isCurrent && 'border-primary bg-primary/5',
                  !isCurrent && 'border-border bg-card hover:border-primary/50',
                  isSelected && 'ring-2 ring-primary ring-offset-1',
                  'cursor-pointer'
                )}
                onClick={() => handleVersionClick(versionInfo.version)}
              >
                {/* Timeline connector - thinner and closer */}
                {index < sortedVersions.length - 1 && (
                  <div className="absolute left-[18px] top-[32px] w-[1px] h-[calc(100%+8px)] bg-border" />
                )}

                <div className="flex items-start gap-2">
                  {/* Version indicator - smaller circle */}
                  <div className={cn(
                    'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold z-10',
                    isCurrent ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  )}>
                    {isCurrent ? <CheckCircle className="h-3 w-3" /> : `v${versionInfo.version}`}
                  </div>

                  {/* Version details - compact single-line layout */}
                  <div className="flex-1 min-w-0">
                    {/* Single line: version info + current badge */}
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-xs font-medium text-foreground truncate flex-1" title={versionInfo.message}>
                        {versionInfo.message}
                      </span>
                      {isCurrent && (
                        <Badge variant="default" className="h-4 px-1.5 text-[9px] shrink-0">
                          Current
                        </Badge>
                      )}
                    </div>

                    {/* Single line: author + timestamp + changes badge */}
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-1.5">
                      <span className="truncate">{versionInfo.author}</span>
                      <span>•</span>
                      <Clock className="h-2.5 w-2.5" />
                      <span className="shrink-0" title={formatFullTimestamp(versionInfo.timestamp)}>
                        {formatTimestamp(versionInfo.timestamp)}
                      </span>
                      <span>•</span>
                      <Badge
                        variant="outline"
                        className={cn(
                          'h-4 px-1 text-[9px] border-0',
                          versionInfo.changes.type === 'create' && 'bg-green-500/10 text-green-600',
                          versionInfo.changes.type === 'edit' && 'bg-primary/10 text-primary',
                          versionInfo.changes.type === 'restore' && 'bg-orange-500/10 text-orange-600'
                        )}
                      >
                        {versionInfo.changes.type === 'create' && '+ Created'}
                        {versionInfo.changes.type === 'edit' && '✎ Edited'}
                        {versionInfo.changes.type === 'restore' && '⟲ Restored'}
                        {versionInfo.changes.elements && versionInfo.changes.elements.length > 0 && (
                          <span className="ml-0.5">
                            {versionInfo.changes.elements.length}
                          </span>
                        )}
                      </Badge>
                    </div>

                    {/* Actions - icon-only buttons */}
                    {!isCurrent && (
                      <div className="flex items-center gap-1">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            onRestore(versionInfo.version);
                          }}
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 hover:bg-primary/10 hover:text-primary"
                          disabled={isRestoring || isDeleting}
                          title="Restore this version"
                        >
                          {isRestoring ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <RotateCcw className="h-3 w-3" />
                          )}
                        </Button>

                        {/* Only allow deleting non-current versions */}
                        {versions.length > 1 && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (confirm(`Delete version ${versionInfo.version}? This cannot be undone.`)) {
                                handleDelete(versionInfo.version);
                              }
                            }}
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 hover:bg-red-50 hover:text-red-600"
                            disabled={isRestoring || isDeleting}
                            title="Delete this version"
                          >
                            {isDeleting ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Trash2 className="h-3 w-3" />
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Footer hint */}
      <div className="flex-shrink-0 p-3 border-t border-border bg-muted/30">
        <p className="text-[10px] text-muted-foreground text-center">
          {selectedVersions.length === 0 ? (
            'Click versions to select for comparison'
          ) : selectedVersions.length === 1 ? (
            'Select one more version to compare'
          ) : (
            'Click Compare button above'
          )}
        </p>
      </div>
    </div>
  );
};
